import { FC, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { Assessment, DomainComparisonEntry, OverallResults } from '@libs/types';

import { useGetAssessmentCompareVersions } from '../../api/assessment-versions-api';
import { DomainResultViewModel, OverallResultsViewModel, RadarComparisonEntry } from '../../types';

import { DomainBarChart } from './DomainBarChart';
import { DomainPieChart } from './DomainPieChart';
import { DomainRadarChart } from './DomainRadarChart';
import { DomainRiskMatrixChart } from './DomainRiskMatrixChart';
import { DomainTreemapChart } from './DomainTreemapChart';

// ── ChartCard ─────────────────────────────────────────────────────────────────

interface ChartCardProps {
  children: React.ReactNode;
  accentColor?: string;
  sx?: SxProps<Theme>;
}

const ChartCard: FC<ChartCardProps> = observer(({ children, accentColor, sx }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...sx,
      }}
    >
      <Box sx={{ height: 3, backgroundColor: accentColor ?? 'primary.main', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Box>
  );
});

// ── ChartsTab ─────────────────────────────────────────────────────────────────

export interface ChartsTabProps {
  domainResults: DomainResultViewModel[];
  overallResults: OverallResultsViewModel;
  comparisonOverall?: OverallResults;
  domainComparisonMap?: Map<string, DomainComparisonEntry>;
  activeVersion?: Assessment;
  baselineVersion?: Assessment;
  allVersions?: Assessment[];
}

const MAX_RADAR_OVERLAYS = 3;

export const ChartsTab: FC<ChartsTabProps> = observer(
  ({
    domainResults,
    overallResults,
    comparisonOverall,
    domainComparisonMap,
    activeVersion,
    baselineVersion,
    allVersions,
  }) => {
    const theme = useTheme();

    // ── Radar version comparison state ───────────────────────────────────────
    const [radarVersionIds, setRadarVersionIds] = useState<string[]>([]);
    const activeId = activeVersion?.id;

    const { data: radarComp0 } = useGetAssessmentCompareVersions(radarVersionIds[0], activeId);
    const { data: radarComp1 } = useGetAssessmentCompareVersions(radarVersionIds[1], activeId);
    const { data: radarComp2 } = useGetAssessmentCompareVersions(radarVersionIds[2], activeId);

    const radarComparisons = useMemo<RadarComparisonEntry[]>(() => {
      const slots = [
        { id: radarVersionIds[0], data: radarComp0 },
        { id: radarVersionIds[1], data: radarComp1 },
        { id: radarVersionIds[2], data: radarComp2 },
      ];

      return slots
        .filter((s): s is { id: string; data: NonNullable<typeof s.data> } => !!s.id && !!s.data)
        .flatMap((s) => {
          const version = allVersions?.find((v) => v.id === s.id);
          if (!version) return [];
          const scoresByDomainId = new Map<string, number>();
          (s.data.domainComparison ?? []).forEach((entry) => {
            scoresByDomainId.set(
              entry.comparisonAssessmentDomain.id,
              entry.baselineResults?.maturity?.percentage ?? 0,
            );
          });
          return [{ version, scoresByDomainId } satisfies RadarComparisonEntry];
        });
    }, [radarVersionIds, radarComp0, radarComp1, radarComp2, allVersions]);

    const handleRadarVersionToggle = (versionId: string) => {
      setRadarVersionIds((prev) => {
        if (prev.includes(versionId)) return prev.filter((id) => id !== versionId);
        if (prev.length >= MAX_RADAR_OVERLAYS) return prev;
        return [...prev, versionId];
      });
    };

    const accent = theme.palette.primary.main;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Row 1: Radar — full width */}
        <ChartCard accentColor={accent} sx={{ height: 800 }}>
          <DomainRadarChart
            domainResults={domainResults}
            activeVersion={activeVersion}
            allVersions={allVersions}
            selectedVersionIds={radarVersionIds}
            onVersionToggle={handleRadarVersionToggle}
            radarComparisons={radarComparisons}
            height={417}
          />
        </ChartCard>

        {/* Row 2: Treemap — full width */}
        <ChartCard accentColor={accent}>
          <DomainTreemapChart domainResults={domainResults} height={460} />
        </ChartCard>

        {/* Row 3: Risk Matrix — full width */}
        <ChartCard accentColor={accent} sx={{ height: 450 }}>
          <DomainRiskMatrixChart domainResults={domainResults} height={360} />
        </ChartCard>

        {/* Row 4: Pie (20%) + Bar (80%) */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 4fr', gap: 2, alignItems: 'stretch' }}>
          <ChartCard accentColor={accent} sx={{ minHeight: 380 }}>
            <DomainPieChart
              overallResults={overallResults}
              comparisonOverall={comparisonOverall}
              activeVersion={activeVersion}
              baselineVersion={baselineVersion}
            />
          </ChartCard>
          <ChartCard accentColor={accent} sx={{ minHeight: 380 }}>
            <DomainBarChart
              domainResults={domainResults}
              domainComparisonMap={domainComparisonMap}
              activeVersion={activeVersion}
              baselineVersion={baselineVersion}
              height={360}
            />
          </ChartCard>
        </Box>

      </Box>
    );
  },
);
